import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { AuthenticatedMobileShell } from '../components/AuthenticatedMobileShell.tsx';
import { CustomerForm } from '../components/CustomerForm.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { WorkspaceContainer } from '../components/WorkspaceLayout.tsx';
import { useMe } from '../lib/auth.ts';
import { useCounterparty, useUpdateCounterparty } from '../lib/counterparties.ts';
import { CUSTOMER_SAVE_ERROR, type UpsertCounterpartyInput } from '../../shared/counterparty.ts';
import { CenteredMessage, CounterpartiesHeader, LoginScreen } from './counterparties.tsx';
import { NotFoundState } from './counterparties.$id.tsx';

export const Route = createFileRoute('/counterparties/$id/edit')({
  component: EditCounterpartyRoute,
});

export function EditCounterpartyRoute(): React.ReactElement {
  const me = useMe();

  if (me.isLoading) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  if (me.data == null) {
    return <LoginScreen />;
  }

  return (
    <AuthenticatedMobileShell currentPath="/counterparties/edit">
      <AuthenticatedEditCounterparty />
    </AuthenticatedMobileShell>
  );
}

function AuthenticatedEditCounterparty(): React.ReactElement {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const detail = useCounterparty(id);
  const updateCounterparty = useUpdateCounterparty();
  const [saveError, setSaveError] = useState<string | null>(null);

  async function save(input: UpsertCounterpartyInput): Promise<void> {
    setSaveError(null);
    try {
      await updateCounterparty.mutateAsync({ id, counterparty: input });
      await navigate({ to: '/counterparties/$id', params: { id }, search: { saved: true } });
    } catch {
      setSaveError(CUSTOMER_SAVE_ERROR);
    }
  }

  if (detail.isPending) {
    return (
      <EditShell title="Edit Customer" backLink={{ to: '/counterparties/$id', params: { id } }}>
        <p className="text-sm text-ink-muted">Loading…</p>
      </EditShell>
    );
  }

  if (detail.isError) {
    return (
      <EditShell title="Edit Customer" backLink={{ to: '/counterparties' }}>
        <NotFoundState />
      </EditShell>
    );
  }

  const customer = detail.data.counterparty;

  return (
    <EditShell
      title={`Edit: ${customer.name}`}
      backLink={{ to: '/counterparties/$id', params: { id } }}
    >
      {saveError ? <StatusMessage tone="error">{saveError}</StatusMessage> : null}
      <CustomerForm
        customer={customer}
        submitLabel="Save"
        submitting={updateCounterparty.isPending}
        onCancel={() => {
          void navigate({ to: '/counterparties/$id', params: { id } });
        }}
        onSubmit={save}
      />
    </EditShell>
  );
}

function EditShell({
  title,
  backLink,
  children,
}: {
  title: string;
  backLink: React.ComponentProps<typeof CounterpartiesHeader>['backLink'];
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="min-h-screen">
      <WorkspaceContainer as="main" className="py-8">
        <div className="space-y-6">
          <CounterpartiesHeader title={title} backLink={backLink} />
          {children}
        </div>
      </WorkspaceContainer>
    </div>
  );
}
